// Tableau de suivi de l'organisation des chantiers.
//
// Une ligne par chantier, une colonne par étape. Vert = fait, rouge = à faire,
// gris = non concerné. On clique une case pour la faire tourner, sans quitter
// l'écran : c'est un tableau de bord d'atelier, pas un formulaire.
//
// Les colonnes s'ajoutent depuis l'écran (« Colonnes »), ce qui évite de
// repasser par le code chaque fois qu'une étape nouvelle apparaît.

import {
  AUCUN_FAIT,
  type ColonneSuivi,
  colonneAutomatique,
  construireSuivi,
  LIBELLES_STATUT_PREP,
  type StatutPrep,
  statutSuivant,
  totauxSuivi,
} from '@imp/shared/suivi-chantiers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Eraser,
  Eye,
  EyeOff,
  Minus,
  Plus,
  Settings2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CHANTIER_STATUT_LABELS } from '@/modules/chantiers/api';
import {
  activerColonneSuivi,
  chargerFaitsChantiers,
  creerColonneSuivi,
  deplacerColonneSuivi,
  estColonneDefaut,
  listChantiersSuivi,
  listColonnesSuivi,
  renommerColonneSuivi,
  setStatutCase,
  viderPrepChantier,
} from '@/modules/chantiers/suivi-api';
import { contactDisplayName, listContacts } from '@/modules/clients/api';

// Apparence d'une case. Le vert et le rouge sont ceux des montants entrants et
// sortants, déjà appris ailleurs dans le CRM.
const CASE_STYLE: Record<StatutPrep, string> = {
  done: 'bg-money-in-soft text-money-in hover:brightness-95',
  todo: 'bg-money-out-soft text-money-out hover:brightness-95',
  na: 'bg-muted text-muted-foreground hover:bg-muted/70',
};

function IconeCase({ statut }: { statut: StatutPrep }) {
  if (statut === 'done') return <Check className="h-4 w-4" aria-hidden />;
  if (statut === 'todo') return <X className="h-4 w-4" aria-hidden />;
  return <Minus className="h-4 w-4" aria-hidden />;
}

function jourCourt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export function SuiviChantiersPage() {
  const queryClient = useQueryClient();
  const [gestionColonnes, setGestionColonnes] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Par défaut on ne montre que les chantiers engagés : ceux dont le client a
  // dit oui. Les autres relèvent du commerce, pas de l'organisation.
  const [engagesSeulement, setEngagesSeulement] = useState(true);
  const [confirmReset, setConfirmReset] = useState(false);

  const colonnesQ = useQuery({ queryKey: ['suivi-colonnes'], queryFn: listColonnesSuivi });
  const chantiersQ = useQuery({ queryKey: ['suivi-chantiers'], queryFn: listChantiersSuivi });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const faitsQ = useQuery({ queryKey: ['suivi-faits'], queryFn: chargerFaitsChantiers });

  const colonnes = useMemo(() => colonnesQ.data ?? [], [colonnesQ.data]);
  const lectureSeule = colonnes.length > 0 && colonnes.every(estColonneDefaut);

  const contactMap = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );
  // null quand le chantier n'a pas de client identifié : la ligne affiche alors
  // le libellé du chantier en tête, plutôt qu'un tiret cliquable.
  const nomClient = (id: string | null): string | null => {
    if (!id) return null;
    const c = contactMap.get(id);
    return c ? contactDisplayName(c) : null;
  };

  const tousLesChantiers = useMemo(
    () =>
      (chantiersQ.data ?? []).map((c) => ({
        id: c.id,
        numero: c.numero,
        libelle: c.libelle,
        statut: c.statut,
        contactId: c.contact_id,
        ville: c.ville,
        datePrevueDebut: c.date_prevue_debut,
        prep: c.metadata?.prep ?? {},
        faits: faitsQ.data?.get(c.id) ?? AUCUN_FAIT,
      })),
    [chantiersQ.data, faitsQ.data],
  );

  const lignes = useMemo(
    () => construireSuivi(tousLesChantiers, colonnes, Date.now(), engagesSeulement),
    [tousLesChantiers, colonnes, engagesSeulement],
  );
  const totaux = useMemo(() => totauxSuivi(lignes), [lignes]);
  /** Combien de chantiers le filtre met de côté : à dire, jamais à cacher. */
  const misDeCote = useMemo(
    () => construireSuivi(tousLesChantiers, colonnes, Date.now(), false).length - lignes.length,
    [tousLesChantiers, colonnes, lignes.length],
  );

  const rafraichir = () => {
    void queryClient.invalidateQueries({ queryKey: ['suivi-chantiers'] });
    void queryClient.invalidateQueries({ queryKey: ['suivi-faits'] });
    void queryClient.invalidateQueries({ queryKey: ['suivi-colonnes'] });
    void queryClient.invalidateQueries({ queryKey: ['alertes'] });
  };

  const caseMut = useMutation({
    mutationFn: (v: { chantierId: string; colonne: ColonneSuivi; statut: StatutPrep }) =>
      setStatutCase(v.chantierId, v.colonne, v.statut),
    onSuccess: rafraichir,
    onError: (e: Error) => {
      setErreur(e.message);
    },
  });

  /**
   * Efface les cases cochées à la main, sur les chantiers affichés seulement :
   * ce qu'on voit est ce qu'on efface, jamais plus.
   */
  const resetMut = useMutation({
    mutationFn: async () => {
      for (const l of lignes) await viderPrepChantier(l.chantier.id);
      return lignes.length;
    },
    onSuccess: () => {
      setConfirmReset(false);
      rafraichir();
    },
    onError: (e: Error) => {
      setErreur(e.message);
    },
  });

  const chargement = colonnesQ.isLoading || chantiersQ.isLoading || faitsQ.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Suivi des chantiers</h1>
          <p className="text-sm text-muted-foreground">
            Où en est la préparation, chantier par chantier. Cliquez une case pour la changer —
            sauf celles que le CRM remplit tout seul.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setConfirmReset((v) => !v);
            }}
          >
            <Eraser className="h-4 w-4" /> Remettre à zéro
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setGestionColonnes((v) => !v);
            }}
          >
            <Settings2 className="h-4 w-4" /> Colonnes
          </Button>
        </div>
      </div>

      {confirmReset ? (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          <p className="text-amber-800 dark:text-amber-200">
            Toutes les cases cochées à la main seront effacées sur les{' '}
            <strong>{lignes.length} chantier{lignes.length > 1 ? 's' : ''} affiché
            {lignes.length > 1 ? 's' : ''}</strong>. Les colonnes remplies par le CRM ne bougent
            pas : elles disent ce qui est vrai. Cette remise à zéro ne se défait pas.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={resetMut.isPending}
              onClick={() => {
                setErreur(null);
                resetMut.mutate();
              }}
            >
              {resetMut.isPending ? 'Effacement…' : 'Oui, tout remettre à zéro'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setConfirmReset(false);
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      ) : null}

      {lectureSeule ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          Colonnes d&apos;origine affichées : la mise à jour du serveur (migration 19) n&apos;est pas
          encore passée. Les cases se cochent normalement, mais l&apos;ajout de colonne attendra.
        </div>
      ) : null}

      {erreur ? (
        <div className="flex items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <span>{erreur}</span>
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => {
              setErreur(null);
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {gestionColonnes ? (
        <GestionColonnes
          colonnes={colonnes}
          lectureSeule={lectureSeule}
          onFait={rafraichir}
          onErreur={setErreur}
          onFermer={() => {
            setGestionColonnes(false);
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={engagesSeulement ? 'default' : 'outline'}
          onClick={() => {
            setEngagesSeulement(true);
          }}
        >
          Chantiers engagés
        </Button>
        <Button
          type="button"
          size="sm"
          variant={engagesSeulement ? 'outline' : 'default'}
          onClick={() => {
            setEngagesSeulement(false);
          }}
        >
          Tous les chantiers
        </Button>
        {engagesSeulement && misDeCote > 0 ? (
          <span className="text-muted-foreground text-xs">
            {misDeCote} chantier{misDeCote > 1 ? 's' : ''} de côté : le client n&apos;a pas encore
            dit oui (devis signé, acompte ou facture).
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Recap libelle="Chantiers suivis" valeur={totaux.chantiers} />
        <Recap libelle="Prêts" valeur={totaux.prets} teinte="text-money-in" />
        <Recap libelle="Rien de renseigné" valeur={totaux.vierges} />
        <Recap libelle="Démarrés, non prêts" valeur={totaux.enRetard} teinte="text-money-out" />
        <Recap libelle="Étapes à faire" valeur={totaux.casesAFaire} />
      </div>

      {chargement ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : lignes.length === 0 ? (
        <Card className="text-muted-foreground space-y-2 p-8 text-center">
          <p>
            {engagesSeulement
              ? 'Aucun chantier engagé pour le moment.'
              : 'Aucun chantier à préparer.'}
          </p>
          <p className="text-xs">
            {engagesSeulement
              ? 'Un chantier apparaît ici dès que le client a dit oui : devis passé en « signé », acompte ou facture émis sur ce chantier.'
              : 'Les chantiers terminés, annulés et les prospects n’apparaissent pas ici.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="sticky left-0 z-10 bg-muted/50 px-4 py-3 font-medium">Client</th>
                <th className="px-3 py-3 text-center font-medium">Début</th>
                {(lignes[0]?.cases ?? []).map((c) => (
                  <th key={c.colonne.id} className="px-3 py-3 text-center font-medium">
                    <span className="block max-w-[7rem] leading-tight">{c.colonne.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.chantier.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="sticky left-0 z-10 bg-card px-4 py-3">
                    <Link
                      to={`/chantiers/${l.chantier.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {nomClient(l.chantier.contactId) ?? l.chantier.libelle}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {nomClient(l.chantier.contactId) === null ? '' : `${l.chantier.libelle} `}
                      {l.chantier.ville ?? ''}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      <span className="font-mono">{l.chantier.numero}</span>
                      {' · '}
                      {CHANTIER_STATUT_LABELS[l.chantier.statut] ?? l.chantier.statut}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <div className={l.enRetard ? 'font-semibold text-money-out' : ''}>
                      {jourCourt(l.chantier.datePrevueDebut)}
                    </div>
                    {l.enRetard ? (
                      <div className="text-[11px] text-money-out">démarré</div>
                    ) : l.joursAvant !== null ? (
                      <div className="text-[11px] text-muted-foreground">
                        dans {l.joursAvant} j
                      </div>
                    ) : null}
                  </td>
                  {l.cases.map((c) => (
                    <td key={c.colonne.id} className="px-3 py-3 text-center">
                      {colonneAutomatique(c.colonne) ? (
                        <span
                          title={`${c.colonne.label} : ${LIBELLES_STATUT_PREP[c.statut]} — rempli par le CRM`}
                          aria-label={`${c.colonne.label} pour ${l.chantier.libelle} : ${LIBELLES_STATUT_PREP[c.statut]} (rempli par le CRM)`}
                          className={`inline-flex h-11 w-11 items-center justify-center rounded-md ring-1 ring-inset ring-current/20 ${CASE_STYLE[c.statut]}`}
                        >
                          <IconeCase statut={c.statut} />
                        </span>
                      ) : (
                        <button
                          type="button"
                          disabled={caseMut.isPending}
                          title={`${c.colonne.label} : ${LIBELLES_STATUT_PREP[c.statut]} — cliquer pour changer`}
                          aria-label={`${c.colonne.label} pour ${l.chantier.libelle} : ${LIBELLES_STATUT_PREP[c.statut]}`}
                          className={`inline-flex h-11 w-11 items-center justify-center rounded-md transition ${CASE_STYLE[c.statut]}`}
                          onClick={() => {
                            setErreur(null);
                            caseMut.mutate({
                              chantierId: l.chantier.id,
                              colonne: c.colonne,
                              statut: statutSuivant(c.statut),
                            });
                          }}
                        >
                          <IconeCase statut={c.statut} />
                        </button>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        <span className="mr-3">
          <Check className="inline h-3 w-3 text-money-in" /> fait
        </span>
        <span className="mr-3">
          <X className="inline h-3 w-3 text-money-out" /> à faire
        </span>
        <span className="mr-3">
          <Minus className="inline h-3 w-3" /> non concerné
        </span>
        <span>
          <span className="ring-current/20 mr-1 inline-block h-3 w-3 rounded-sm align-middle ring-1 ring-inset" />
          case encadrée : remplie par le CRM, elle ne se coche pas
        </span>
      </p>
    </div>
  );
}

function Recap({
  libelle,
  valeur,
  teinte,
}: {
  libelle: string;
  valeur: number;
  teinte?: string;
}) {
  return (
    <Card className="p-4">
      <div className="text-xs uppercase text-muted-foreground">{libelle}</div>
      <div className={`text-2xl font-bold ${teinte ?? ''}`}>{valeur}</div>
    </Card>
  );
}

function GestionColonnes({
  colonnes,
  lectureSeule,
  onFait,
  onErreur,
  onFermer,
}: {
  colonnes: ColonneSuivi[];
  lectureSeule: boolean;
  onFait: () => void;
  onErreur: (m: string) => void;
  onFermer: () => void;
}) {
  const [nouveau, setNouveau] = useState('');
  const [alerte, setAlerte] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const echec = (e: Error) => {
    onErreur(e.message);
  };
  const creerMut = useMutation({
    mutationFn: () => creerColonneSuivi(nouveau, alerte),
    onSuccess: () => {
      setNouveau('');
      setAlerte('');
      onFait();
    },
    onError: echec,
  });
  const renommerMut = useMutation({
    mutationFn: (v: { id: string; label: string }) => renommerColonneSuivi(v.id, v.label),
    onSuccess: () => {
      setEditId(null);
      onFait();
    },
    onError: echec,
  });
  const activerMut = useMutation({
    mutationFn: (v: { id: string; actif: boolean }) => activerColonneSuivi(v.id, v.actif),
    onSuccess: onFait,
    onError: echec,
  });
  const deplacerMut = useMutation({
    mutationFn: (v: { id: string; sens: -1 | 1 }) => deplacerColonneSuivi(v.id, v.sens),
    onSuccess: onFait,
    onError: echec,
  });

  const rangees = colonnes.slice().sort((a, b) => a.ordre - b.ordre);

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Colonnes du tableau</h2>
        <Button variant="ghost" size="icon" aria-label="Fermer" onClick={onFermer}>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {rangees.map((c, i) => (
          <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
            {editId === c.id ? (
              <>
                <Input
                  value={editLabel}
                  className="max-w-xs"
                  aria-label="Nouveau nom de la colonne"
                  onChange={(e) => {
                    setEditLabel(e.target.value);
                  }}
                />
                <Button
                  size="sm"
                  disabled={renommerMut.isPending}
                  onClick={() => {
                    renommerMut.mutate({ id: c.id, label: editLabel });
                  }}
                >
                  Enregistrer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditId(null);
                  }}
                >
                  Annuler
                </Button>
              </>
            ) : (
              <>
                <span className={`flex-1 ${c.actif ? '' : 'text-muted-foreground line-through'}`}>
                  {c.label}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Déplacer ${c.label} vers la gauche`}
                  disabled={lectureSeule || i === 0 || deplacerMut.isPending}
                  onClick={() => {
                    deplacerMut.mutate({ id: c.id, sens: -1 });
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Déplacer ${c.label} vers la droite`}
                  disabled={lectureSeule || i === rangees.length - 1 || deplacerMut.isPending}
                  onClick={() => {
                    deplacerMut.mutate({ id: c.id, sens: 1 });
                  }}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={c.actif ? `Masquer ${c.label}` : `Afficher ${c.label}`}
                  disabled={lectureSeule || activerMut.isPending}
                  onClick={() => {
                    activerMut.mutate({ id: c.id, actif: !c.actif });
                  }}
                >
                  {c.actif ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={lectureSeule}
                  onClick={() => {
                    setEditId(c.id);
                    setEditLabel(c.label);
                  }}
                >
                  Renommer
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-2 border-t pt-3">
        <Label htmlFor="col_nouvelle">Ajouter une colonne</Label>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            id="col_nouvelle"
            className="max-w-xs"
            placeholder="Ex : Échafaudage commandé"
            value={nouveau}
            onChange={(e) => {
              setNouveau(e.target.value);
            }}
          />
          <Input
            className="max-w-sm"
            placeholder="Phrase de l'alerte (facultatif)"
            aria-label="Phrase de l'alerte"
            value={alerte}
            onChange={(e) => {
              setAlerte(e.target.value);
            }}
          />
          <Button
            disabled={lectureSeule || creerMut.isPending || nouveau.trim() === ''}
            onClick={() => {
              creerMut.mutate();
            }}
          >
            <Plus className="h-4 w-4" /> Ajouter
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Une colonne masquée garde ce qui a été coché : elle réapparaît telle quelle.
        </p>
      </div>
    </Card>
  );
}
